import styled from 'styled-components';
import tw from 'twin.macro';

import { ListBox } from '@/components/ListBox/ListBox';
import type { Sort } from '@/types';

type FeedSortType = {
  /**
   * dictionary of sort options
   */
  sortMap: Sort;
  /**
   * selected sort option
   */
  sortOption: string;
  /**
   * sort change handler
   */
  sortHandler: (sort: string) => void;
};

const SORT_MAP: Record<string, { sort: string; order: string }> = {
  relevance: { sort: 'score', order: 'desc' },
  dateDsc: { sort: 'timestamp', order: 'desc' },
  dateAsc: { sort: 'timestamp', order: 'asc' },
  updatedDateDsc: { sort: 'updated_timestamp', order: 'desc' },
  updatedDateAsc: { sort: 'updated_timestamp', order: 'asc' },
  sizeDsc: { sort: 'size', order: 'desc' },
  sizeAsc: { sort: 'size', order: 'asc' },
  attachmentsDsc: { sort: 'attachments', order: 'desc' },
  attachmentsAsc: { sort: 'attachments', order: 'asc' },
  companyDsc: { sort: 'company', order: 'desc' },
  companyAsc: { sort: 'company', order: 'asc' },
  categoryDsc: { sort: 'category', order: 'desc' },
  categoryAsc: { sort: 'category', order: 'asc' },
};

const DisplayNameMap = {
  relevance: 'Relevance',
  dateDsc: 'Latest First',
  dateAsc: 'Oldest First',
  updatedDateDsc: 'Latest Updated First',
  updatedDateAsc: 'Oldest Updated First',
  sizeDsc: 'Size: Largest First',
  sizeAsc: 'Size: Smallest First',
  attachmentsDsc: 'With Attachments',
  attachmentsAsc: 'Without Attachments',
  companyDsc: 'Company (Z -> A)',
  companyAsc: 'Company (A -> Z)',
  categoryDsc: 'Category (Z -> A)',
  categoryAsc: 'Category (A -> Z)',
} as const;

const isDisplayNameMapKey = (key: string): key is keyof typeof DisplayNameMap =>
  Object.keys(DisplayNameMap).includes(key);

const generateOptions = (sortMap: Sort) => {
  return Object.keys(sortMap).reduce(
    (acc: Record<string, string>[], sortKey) => {
      if (isDisplayNameMapKey(sortKey)) {
        return [
          ...acc,
          {
            key: DisplayNameMap[sortKey],
            value: sortKey,
          },
        ];
      }
      return acc;
    },
    []
  );
};

const Container = styled.div`
  ${tw`flex-shrink-0`}

  [data-reach-listbox-button] {
    ${tw`body-xs-bold py-2 pl-2 pr-1`}
  }

  [data-reach-listbox-arrow] > span {
    ${tw`text-gray-700 w-4 h-4`}
  }
`;

const FeedSort = ({ sortMap, sortOption, sortHandler }: FeedSortType) => {
  if (Object.keys(sortMap).length === 0) {
    return null;
  }

  return (
    <Container>
      <ListBox
        label='Sort'
        options={generateOptions(sortMap)}
        getOptionLabel={(option) => option['key']}
        renderOption={(option) => <>{option['key']}</>}
        selectedOption={(option, selectedValue) =>
          option['value'] === selectedValue
        }
        uniqueKey='value'
        highlighted
        value={sortOption}
        onChange={sortHandler}
        noAnimation
        popoverPosition='right'
      />
    </Container>
  );
};

export type { FeedSortType };
export { FeedSort, SORT_MAP };
